/**
 * t184u: reactions on a feed item itself (an announcement, issue, task, event, shift offer or acknowledgement), for
 * every card type on the phone and on a computer. Pills for what's there, plus a smiley that opens the five quick
 * reactions. Reads are batched per type for a screenful of cards (one request); kudos and chat messages use their own
 * reactions (the same ones their pages show), everything else `collab.entity_reactions` through the item's thread
 * access rules. t255u: the summary beside the pills (faces and the total) or a long-press on a pill shows who reacted
 * (a "Reactions" bottom sheet on the phone, a popover on a computer). t295u: the summary is the bar's muted "4 people".
 */
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { SmilePlus } from 'lucide-react';
import type { ReactionSummary } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useWorkspaceId } from '@/lib/hooks';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { ReactionBar } from './ReactionBar';
import { ReactionsPopover, ReactionsSheet } from './ReactorsList';

export const ITEM_REACTIONS = ['👍', '❤️', '👏', '🎉', '😂'];
// t187u: shared with the realtime patch (packages/data), so live updates land in the same cache entry.
export { itemReactionsKey } from '@tradeoutlet/data';
import { itemReactionsKey } from '@tradeoutlet/data';

type Fetch = (ws: string, type: string, ids: string[]) => Promise<{ reactions: Record<string, ReactionSummary[]> }>;
/** Every card rendered in the same tick asks once per type. */
export function createItemReactionBatcher(fetcher: Fetch, delayMs = 25) {
  const pending = new Map<string, { id: string; resolve: (r: ReactionSummary[]) => void; reject: (e: unknown) => void }[]>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  const flush = () => {
    timer = null;
    const batches = [...pending]; pending.clear();
    for (const [key, reqs] of batches) {
      const [ws, type] = key.split('|') as [string, string];
      const ids = [...new Set(reqs.map(r => r.id))];
      const chunks: string[][] = [];
      for (let i = 0; i < ids.length; i += 100) chunks.push(ids.slice(i, i + 100));
      Promise.all(chunks.map(c => fetcher(ws, type, c)))
        .then(parts => { const all = Object.assign({}, ...parts.map(p => p.reactions)) as Record<string, ReactionSummary[]>; reqs.forEach(r => r.resolve(all[r.id] ?? [])); })
        .catch(e => reqs.forEach(r => r.reject(e)));
    }
  };
  return {
    load(ws: string, type: string, id: string): Promise<ReactionSummary[]> {
      return new Promise((resolve, reject) => {
        const k = `${ws}|${type}`;
        pending.set(k, [...(pending.get(k) ?? []), { id, resolve, reject }]);
        if (!timer) timer = setTimeout(flush, delayMs);
      });
    },
  };
}
const batcher = createItemReactionBatcher((ws, type, ids) => api.itemReactions(ws, type, ids));

export function useItemReactions(type: string, id: string) {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: itemReactionsKey(ws ?? '', type, id), queryFn: () => batcher.load(ws!, type, id), enabled: Boolean(ws && id), staleTime: 30_000 });
}

/** My reaction added or taken away, applied straight away and replaced by what the server says. */
export function toggledReactions(list: ReactionSummary[], emoji: string, me: string): ReactionSummary[] {
  const r = list.find(x => x.emoji === emoji);
  if (!r) return [...list, { emoji, count: 1, user_ids: [me] }];
  const mine = r.user_ids.includes(me);
  const next = mine ? { ...r, count: r.count - 1, user_ids: r.user_ids.filter(u => u !== me) } : { ...r, count: r.count + 1, user_ids: [...r.user_ids, me] };
  return list.map(x => (x === r ? next : x)).filter(x => x.count > 0);
}

/** t189u: the pills of one item and my toggle (optimistic, then the server's answer); shared by the card bar and long-press sheets. */
export function useItemReactionToggle(type: string, id: string) {
  const ws = useWorkspaceId();
  const { userId } = useAuth();
  const toast = useUi(s => s.toast);
  const q = useItemReactions(type, id);
  const pills = q.data ?? [];
  const toggle = async (emoji: string) => {
    if (!ws || !userId) return;
    const key = itemReactionsKey(ws, type, id);
    const before = (queryClient.getQueryData(key) as ReactionSummary[] | undefined) ?? pills;
    const mine = Boolean(before.find(r => r.emoji === emoji)?.user_ids.includes(userId));
    queryClient.setQueryData(key, toggledReactions(before, emoji, userId));
    try {
      const next = type === 'kudos' ? (await (mine ? api.unreactKudos(id, emoji) : api.reactKudos(id, emoji))).reactions
        : type === 'message' ? (await (mine ? api.removeReaction(id, emoji) : api.addReaction(id, emoji))).reactions
        : (await (mine ? api.unreactItem(ws, type, id, emoji) : api.reactItem(ws, type, id, emoji))).reactions;
      queryClient.setQueryData(key, next.map(r => ({ emoji: r.emoji, count: r.count, user_ids: r.user_ids })));
    } catch (e) { queryClient.setQueryData(key, before); toast(errorMessage(e), 'error'); }
  };
  const mine = (emoji: string) => Boolean(userId && pills.find(r => r.emoji === emoji)?.user_ids.includes(userId));
  return { pills, toggle, mine };
}

export function ItemReactions({ type, id, size = 'sm', className, pillsOnly, canAdd = true }: { type: string; id: string; size?: 'sm' | 'lg'; className?: string; pillsOnly?: boolean;
  /** t522u: false = reactions are turned off for you here: pills show, you can only take yours back. */ canAdd?: boolean }) {
  const { pills, toggle: toggleIt } = useItemReactionToggle(type, id);
  const { userId } = useAuth();
  const [picking, setPicking] = useState(false);
  const [who, setWho] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!picking) return;
    const down = (e: PointerEvent) => { if (!box.current?.contains(e.target as Node)) setPicking(false); };
    window.addEventListener('pointerdown', down);
    return () => window.removeEventListener('pointerdown', down);
  }, [picking]);
  const toggle = async (emoji: string) => {
    setPicking(false);
    if (!canAdd && !pills.find(p => p.emoji === emoji)?.user_ids.includes(userId ?? '')) return;
    await toggleIt(emoji);
  };
  const onlyPills = pillsOnly || !canAdd;
  const kind = type === 'kudos' ? 'kudos' : type === 'message' ? 'message' : 'entity';
  const big = size === 'lg';
  const rid = kind === 'entity' ? `${type}:${id}` : id;
  if (onlyPills && !pills.length) return null;
  return (
    <div ref={box} className={clsx('relative flex min-w-0 flex-wrap items-center gap-1', className)} data-testid="item-reactions" data-type={type}>
      {pills.length > 0 && <ReactionBar kind={kind} id={rid} pills={pills} onToggle={e => void toggle(e)} size={big ? 'lg' : 'sm'} onShowReactors={setWho} />}
      {who && (big ? <ReactionsSheet kind={kind} id={rid} pills={pills} initial={who} onClose={() => setWho(null)} />
        : <ReactionsPopover kind={kind} id={rid} pills={pills} initial={who} onClose={() => setWho(null)} />)}
      {!onlyPills && <button type="button" onClick={() => setPicking(v => !v)} aria-label="Add reaction" aria-expanded={picking} data-testid="item-react"
        // t295u: quieter - muted on the phone, and on a computer it appears when you hover the card (or tab to it).
        className={clsx('react-quiet flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-200', big ? 'h-11 w-11' : 'h-7 w-7')}>
        <SmilePlus className={big ? 'h-5 w-5' : 'h-4 w-4'} aria-hidden />
      </button>}
      {picking && (
        <div role="menu" aria-label="React" data-testid="item-react-picker"
          className="absolute bottom-full left-0 z-30 mb-1 flex gap-0.5 rounded-full bg-white p-1 shadow-lg ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
          {ITEM_REACTIONS.map(e => (
            <button key={e} type="button" role="menuitem" onClick={() => void toggle(e)} aria-label={`React with ${e}`}
              className={clsx('grid place-items-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-700', big ? 'h-11 w-11 text-xl' : 'h-8 w-8 text-lg')}>{e}</button>
          ))}
        </div>
      )}
    </div>
  );
}
